import { z } from 'zod';
import {
  claimStatusTone,
  compareCivilDates,
  daysBetween,
  formatCurrency,
  isCivilDate,
  type CivilDate,
  type ClaimApi,
  type ClaimStatusTone,
  type useCoverageAtDate,
} from '@eis/core';

/**
 * The claims feature's pure logic (SDD-018 §9, ui-spec §7): the rows the list and the tabs show,
 * "Tage offen", the coverage terms in words, how a policy is named for the coverage check, and the
 * create form's v1 validation. Nothing here renders and nothing here fetches.
 */

export type Claim = ClaimApi.ClaimClass;
/** `GET /v1/policies:coverageAtDate`'s answer (SDD-018 §7), as `@eis/core` types it. */
export type Coverage = NonNullable<ReturnType<typeof useCoverageAtDate>['data']>;
export type CoverageDetail = NonNullable<Coverage['coverage']>;
export type CoverageCategory = NonNullable<CoverageDetail['categories']>[number];
export type CoverageTerm = NonNullable<CoverageCategory['term']>;
export type CoverageDeductible = NonNullable<CoverageDetail['deductibles']>[number];
export type InsuredObject = NonNullable<Coverage['insuredObjects']>[number];

/** The four verdicts (SDD-018 §7, DV-25: a string on the wire). */
export const VERDICTS = ['COVERED', 'IN_WAITING_PERIOD', 'NOT_IN_FORCE', 'NO_SUCH_OBJECT'] as const;
export type Verdict = (typeof VERDICTS)[number];

/** A wire instant or date (`2026-03-10T00:00:00Z`) as the civil date it names; '' when absent. */
export function civilOf(value: string | undefined | null): CivilDate {
  const date = (value ?? '').slice(0, 10);
  return isCivilDate(date) ? date : '';
}

/** Statuses after which a claim is no longer open — its "Tage offen" stop at the last update. */
const FINISHED = new Set(['closed', 'rejected']);

/**
 * v1's "Tage offen" / "Tage seit Schadeneröffnung": whole days from the claim's opening (its
 * `createdAt`) to today — or, for a finished claim, to its last update. Never negative.
 */
export function daysOpen(
  claim: Pick<Claim, 'createdAt' | 'updatedAt' | 'status'>,
  today: CivilDate,
): number {
  const from = civilOf(claim.createdAt);
  if (from === '') return 0;
  const until = FINISHED.has(claim.status) ? civilOf(claim.updatedAt) : today;
  if (until === '') return 0;
  return Math.max(0, daysBetween(from, until));
}

/**
 * A claimservice amount as a number, or undefined when the claim has none. The image answers
 * `null` for a claim nobody has costed yet, although its OpenAPI document types the field
 * `number` — observed on the local stack 2026-10-04 (CL-2/CL-3 `initialCost: null`).
 */
export function amountOf(value: number | null | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** A claimservice amount (a decimal number of euros) through core formatCurrency (rule 6). */
export function formatAmount(
  amount: number | null | undefined,
  locale: string,
  currency = 'EUR',
): string {
  if (amount === undefined || amount === null || Number.isNaN(amount)) return '—';
  return formatCurrency(Math.round(amount * 100), currency, locale);
}

/**
 * Whether a claim is booked on a group policy (C-8): a member's `accountCode` is its group
 * policy's code, a standalone policy's is its policyholder's partner code — the partner linked as
 * the liable party. So an account that names none of the claim's partners is a group policy.
 * Without partners on the row there is nothing to tell by, and the claim is not marked.
 */
export function isGroupAccount(claim: Pick<Claim, 'accountCode' | 'claimPartners'>): boolean {
  const partners = claim.claimPartners ?? [];
  if (claim.accountCode === '' || partners.length === 0) return false;
  return !partners.some((partner) => partner.partnerCode === claim.accountCode);
}

export interface ClaimRow {
  readonly id: string;
  readonly code: string;
  readonly claimNumber: string;
  readonly policyCode: string;
  readonly policyObjectCode: string;
  readonly objectName: string;
  readonly claimType: string;
  readonly accountCode: string;
  readonly groupAccount: boolean;
  readonly damageDate: CivilDate;
  readonly notificationDate: CivilDate;
  readonly daysOpen: number;
  readonly status: string;
  readonly tone: ClaimStatusTone;
  readonly adjuster: string;
  readonly title: string;
  readonly description: string;
  /** Undefined when claimservice holds none (it answers `null`, see amountOf). */
  readonly initialCost: number | undefined;
  readonly reimbursementAmount: number | undefined;
}

/** `P-10442#bike` → `bike` (DV-33: the policy object code is `<policy>#<object>`). */
export function objectNameOf(policyObjectCode: string): string {
  const hash = policyObjectCode.lastIndexOf('#');
  return hash < 0 ? policyObjectCode : policyObjectCode.slice(hash + 1);
}

export function toClaimRow(claim: Claim, today: CivilDate): ClaimRow {
  return {
    id: claim.code,
    code: claim.code,
    claimNumber: claim.claimNumber === '' ? claim.code : claim.claimNumber,
    policyCode: claim.policyCode,
    policyObjectCode: claim.policyObjectCode,
    objectName: objectNameOf(claim.policyObjectCode),
    claimType: claim.claimType,
    accountCode: claim.accountCode,
    groupAccount: isGroupAccount(claim),
    damageDate: civilOf(claim.damageDate),
    notificationDate: civilOf(claim.notificationDate),
    daysOpen: daysOpen(claim, today),
    status: claim.status,
    tone: claimStatusTone(claim.status),
    adjuster: claim.adjuster ?? '',
    title: claim.title,
    description: claim.description,
    initialCost: amountOf(claim.initialCost),
    reimbursementAmount: amountOf(claim.reimbursementAmount),
  };
}

export function toClaimRows(claims: readonly Claim[], today: CivilDate): ClaimRow[] {
  return claims.map((claim) => toClaimRow(claim, today));
}

/** The claimed total of the rows a screen HAS loaded (labelled as such, never a service total). */
export function claimedTotal(rows: readonly Pick<ClaimRow, 'initialCost'>[]): number {
  return rows.reduce((sum, row) => sum + Math.round((row.initialCost ?? 0) * 100), 0) / 100;
}

/** An amount cell as v1 shows it: no amount (or 0) reads as `none` ("Nicht eingereicht"). */
export function amountText(
  amount: number | null | undefined,
  locale: string,
  none: string,
): string {
  const value = amountOf(amount);
  return value === undefined || value === 0 ? none : formatAmount(value, locale);
}

// ---------------------------------------------------------------------------------------------
// Coverage in words
// ---------------------------------------------------------------------------------------------

/** A coverage term (v1 CoverageTerm), structured for the screen to put into words. */
export type TermDescription =
  | { readonly kind: 'notCovered' }
  | { readonly kind: 'fixed'; readonly amount: number; readonly period: string }
  | {
      readonly kind: 'percent';
      readonly percent: number;
      readonly limit: number | undefined;
      readonly period: string;
    };

export function describeTerm(term: CoverageTerm | undefined): TermDescription {
  if (term === undefined || term.isCovered === false) return { kind: 'notCovered' };
  const value = term.value ?? {};
  const period = term.period ?? '';
  if (term.type === 'percent') {
    return { kind: 'percent', percent: value.amount ?? 0, limit: value.limit, period };
  }
  return { kind: 'fixed', amount: value.amount ?? 0, period };
}

/** The categories a claim may be filed under at that date (C-11): covered and not technical. */
export function offerableCategories(coverage: Coverage | undefined): CoverageCategory[] {
  return (coverage?.coverage?.categories ?? []).filter((category) => category.offerable === true);
}

/** A category's label, or its key when the document gave none. */
export function categoryLabel(category: Pick<CoverageCategory, 'key' | 'label'>): string {
  return category.label === undefined || category.label === ''
    ? (category.key ?? '')
    : category.label;
}

/**
 * Whether the member's group tarif decided this section (`sources`, SDD-018 §7): the key is the
 * resolve endpoint's — `categories.reparatur`, `deductibles`, `waitingPeriod`, `approvalLimits` —
 * under the selected variant.
 */
export function isFromGroup(coverage: Coverage | undefined, section: string): boolean {
  const variant = coverage?.variant?.key ?? '';
  return coverage?.sources?.[`tariffVariations.${variant}.${section}`] === 'GROUP_TARIF';
}

/** The verdict as one of the four words; an empty one is read as COVERED (the proto default). */
export function verdictOf(coverage: Pick<Coverage, 'verdict'> | undefined): Verdict {
  const verdict = coverage?.verdict ?? '';
  return (VERDICTS as readonly string[]).includes(verdict) ? (verdict as Verdict) : 'COVERED';
}

// ---------------------------------------------------------------------------------------------
// Naming the policy (SDD-018 §9: code, or plate / frame number at the damage date)
// ---------------------------------------------------------------------------------------------

export const LOOKUP_MODES = ['policyCode', 'plate', 'frame', 'vehiclePlate', 'vin'] as const;
export type LookupMode = (typeof LOOKUP_MODES)[number];

/**
 * The identifier fields the lookup speaks (SDD-017 identifiers): the bike's insurance plate and
 * frame number, and — since the motor fleet (SDD-022 D-1) — the car's licence plate and VIN. A
 * claim starts from the object at the damage date (SDD-018 R13), whatever the product.
 */
const IDENTIFIER_FIELDS: Readonly<Record<Exclude<LookupMode, 'policyCode'>, string>> = {
  plate: 'data.bike.insurance_plate',
  frame: 'data.bike.frame_number',
  vehiclePlate: 'data.vehicle.license_plate',
  vin: 'data.vehicle.vin',
};

export interface PolicyReference {
  readonly policyCode?: string;
  readonly identifier?: string;
}

export function policyReference(mode: LookupMode, value: string): PolicyReference | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  if (mode === 'policyCode') return { policyCode: trimmed };
  return { identifier: `${IDENTIFIER_FIELDS[mode]}==${trimmed}` };
}

/**
 * The candidates of a `409 AMBIGUOUS_IDENTIFIER` (DV-26: the ErrorInfo metadata `codes`,
 * comma-separated), or undefined when the error is something else. Read structurally: the SDK may
 * hand a 409 over as its ConflictError or as an ApiError, and both carry `status` and `body`.
 */
export function ambiguousCodesOf(error: unknown): string[] | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const { status, body } = error as { status?: unknown; body?: unknown };
  if (status !== 409 || typeof body !== 'object' || body === null) return undefined;
  const details = (body as { details?: unknown }).details;
  if (!Array.isArray(details)) return undefined;
  for (const detail of details as unknown[]) {
    if (typeof detail !== 'object' || detail === null) continue;
    const info = detail as { reason?: unknown; metadata?: { codes?: unknown } };
    if (info.reason !== 'AMBIGUOUS_IDENTIFIER') continue;
    const codes = info.metadata?.codes;
    return typeof codes === 'string'
      ? codes
          .split(',')
          .map((code) => code.trim())
          .filter((code) => code !== '')
      : [];
  }
  return undefined;
}

/** DV-33: an object claim names its object as `<policy>#<object>`, never by a bridge integer. */
export function policyObjectCodeOf(policyCode: string, objectName: string): string {
  return `${policyCode}#${objectName}`;
}

// ---------------------------------------------------------------------------------------------
// The create form (ui-spec §7: v1's validation kept)
// ---------------------------------------------------------------------------------------------

/** Messages are the claims namespace's keys; the screen translates them. */
export const CREATE_MESSAGES = {
  required: 'create.validation.required',
  notificationInFuture: 'create.validation.notificationInFuture',
  endBeforeDamage: 'create.validation.endBeforeDamage',
  invalidDate: 'create.validation.invalidDate',
} as const;

const civilDate = z
  .string()
  .min(1, CREATE_MESSAGES.required)
  .refine((value) => isCivilDate(value), CREATE_MESSAGES.invalidDate);

export function createClaimSchema(context: {
  readonly today: CivilDate;
  readonly damageDate: CivilDate;
}) {
  return z
    .object({
      policyObjectCode: z.string().min(1, CREATE_MESSAGES.required),
      claimType: z.string().min(1, CREATE_MESSAGES.required),
      title: z.string().trim().min(1, CREATE_MESSAGES.required),
      description: z.string(),
      notificationDate: civilDate,
      damageEndDate: z
        .string()
        .refine((value) => value === '' || isCivilDate(value), CREATE_MESSAGES.invalidDate),
    })
    .superRefine((values, ctx) => {
      if (
        isCivilDate(values.notificationDate) &&
        compareCivilDates(values.notificationDate, context.today) > 0
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['notificationDate'],
          message: CREATE_MESSAGES.notificationInFuture,
        });
      }
      if (
        values.damageEndDate !== '' &&
        isCivilDate(values.damageEndDate) &&
        isCivilDate(context.damageDate) &&
        compareCivilDates(values.damageEndDate, context.damageDate) < 0
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['damageEndDate'],
          message: CREATE_MESSAGES.endBeforeDamage,
        });
      }
    });
}

export type CreateClaimValues = z.infer<ReturnType<typeof createClaimSchema>>;

/** A civil date as claimservice's instant (`2026-03-10T00:00:00Z`, claims.seed.json). */
export const toWireDate = (date: CivilDate): string => `${date}T00:00:00Z`;

/** The claimservice request (claims.seed.json's shape): status `reported`, no number. */
export function toCreateRequest(
  values: CreateClaimValues,
  policyCode: string,
  damageDate: CivilDate,
): ClaimApi.CreateClaimRequestDto {
  return {
    policyCode,
    policyObjectCode: values.policyObjectCode,
    damageDate: toWireDate(damageDate),
    notificationDate: toWireDate(values.notificationDate),
    ...(values.damageEndDate === '' ? {} : { damageEndDate: toWireDate(values.damageEndDate) }),
    claimType: values.claimType,
    title: values.title.trim(),
    description: values.description,
    status: 'reported',
    customFields: {},
  };
}

/** The claim partner role C-11 links the liable party under. */
export const LIABLE_ROLE_NAME = 'Versicherungsnehmer';
